export type SubscriptionPlan = 'free' | 'basic' | 'premium';

export interface SubscriptionTier {
  id: SubscriptionPlan;
  name: string;
  priceLabel: string;
  description: string;
  features: string[];
}

export const SUBSCRIPTION_TIERS: SubscriptionTier[] = [
  {
    id: 'free',
    name: 'Free',
    priceLabel: '€0',
    description: 'Create and edit pitch decks manually.',
    features: [
      'Manual pitch deck creation',
      'Manual slide editing',
      'Browse industry templates',
      'Present and preview decks',
    ],
  },
  {
    id: 'basic',
    name: 'Basic',
    priceLabel: '€10.99/mo',
    description: 'Everything in Free, plus AI-powered deck building.',
    features: [
      'Everything in Free',
      'AI deck generation from a prompt',
      'AI slide editing in the editor',
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    priceLabel: '€14.99/mo',
    description: 'Full access for teams that pitch often.',
    features: [
      'Everything in Basic',
      'Priority AI generation',
      'Premium templates (coming soon)',
      'Team features (coming soon)',
    ],
  },
];

export const normalizePlan = (plan?: string | null): SubscriptionPlan => {
  if (plan === 'basic' || plan === 'premium') {
    return plan;
  }
  return 'free';
};

export const canUseAi = (plan?: string | null): boolean => {
  const normalized = normalizePlan(plan);
  return normalized === 'basic' || normalized === 'premium';
};

export const getTier = (plan: SubscriptionPlan): SubscriptionTier =>
  SUBSCRIPTION_TIERS.find((tier) => tier.id === plan) ?? SUBSCRIPTION_TIERS[0];

export const planDisplayName = (plan?: string | null): string => getTier(normalizePlan(plan)).name;

const PLAN_RANK: Record<SubscriptionPlan, number> = { free: 0, basic: 1, premium: 2 };

export const isUpgradeTarget = (
  currentPlan: SubscriptionPlan,
  targetPlan: SubscriptionPlan,
): boolean => PLAN_RANK[targetPlan] > PLAN_RANK[currentPlan];

export const canSelectPlan = (
  currentPlan: SubscriptionPlan,
  targetPlan: SubscriptionPlan,
): 'current' | 'upgrade' | 'included' => {
  if (targetPlan === currentPlan) {
    return 'current';
  }
  if (PLAN_RANK[targetPlan] > PLAN_RANK[currentPlan]) {
    return 'upgrade';
  }
  return 'included';
};
